import { Drawer } from 'almanac-ui/Drawer/Drawer'
import { observer } from 'mobx-react-lite'
import Styles from '../Panels.module.scss'
import ReviewSubmissionPanelStyles from './ReviewSubmissionPanel.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useEffect, useRef, useState } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { reviewResult } from 'components/ReviewSummaryModal/utils'
import cx from 'classnames'
import { ContentComposer } from 'components/ShareSheet/ContentComposer/ContentComposer'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import {
  InformalFeedbackRequest,
  InformalFeedbackReviewDismissReason,
  InformalFeedbackReviewScore,
} from 'api/clients/InformalFeedbackRequestsClient'
import { useAuth } from 'hooks/UseAuth'
import { SubmitButton } from './SubmitButton'
import { useInformalFeedbackRequests } from 'hooks/almanacFiles/useInformalFeedbackRequests'
import { useRootStore } from 'stores/RootStore'
import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { displayDaysRemaining } from 'utils/informalFeedbackRequest'
import { Calendar2 } from 'blaze-ui/icons'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

dayjs.extend(minMax)

interface ScoreProps {
  value: number | string
  label: string
  description: string
  isSelected: boolean
  onPress: () => void
}

const Score = ({ value, label, description, isSelected, onPress }: ScoreProps) => (
  <div
    onClick={onPress}
    className={cx(ReviewSubmissionPanelStyles.score, { [ReviewSubmissionPanelStyles.isSelected]: isSelected })}
  >
    <div className={ReviewSubmissionPanelStyles.icon}>{reviewResult[value].iconLarge}</div>
    <div className={ReviewSubmissionPanelStyles.info}>
      <Text variant="smallList" color="var(--dark-80)">
        {label}
      </Text>
      <Text variant="placeholder" color="var(--dark-60)">
        {description}
      </Text>
    </div>
  </div>
)

export const ReviewSubmissionPanel = observer(({ initialRequest }: { initialRequest?: InformalFeedbackRequest }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const article = useCurrentArticleMeta()
  const { isMobile } = useResponsiveDesign()
  const { designStore, rightRailPanelStore } = useRootStore()
  const auth = useAuth()
  const almanacFile = article?.almanac_file || designStore?.file
  const { openRequest, isLoading } = useInformalFeedbackRequests(almanacFile?.id || '', {
    enabled: !!almanacFile && !initialRequest,
  })
  const request = initialRequest || openRequest
  const review = request?.reviews?.filter(
    ({ reviewer, dismissed_reason }) => reviewer.id === auth?.id && !dismissed_reason,
  )[0]

  const contentRef = useRef(null)
  const [score, setScore] = useState<InformalFeedbackReviewScore | InformalFeedbackReviewDismissReason | ''>('')
  const [message, setMessage] = useState<string>('')
  const [dueDate, setDueDate] = useState<string>(
    dayjs.max(dayjs().add(2, 'days'), dayjs(request?.ends_on)).format('YYYY-MM-DD'),
  )

  useEffect(() => {
    if (!!request && !review) rightRailPanelStore.hide()
  }, [request, review, rightRailPanelStore])

  if (!review) return null

  return (
    <>
      <ResponsiveDesign.Desktop>
        <Drawer.Header>
          <div className={Styles.header}>
            <div className={Styles.headerInner}>
              {isSystemRedesignEnabled ? (
                <Text>Finish your review</Text>
              ) : (
                <Heading level={4} lineClamp={1}>
                  Finish your review
                </Heading>
              )}
            </div>
            <Drawer.CloseButton />
          </div>
        </Drawer.Header>
      </ResponsiveDesign.Desktop>
      <div className={ReviewSubmissionPanelStyles.wrapper}>
        <Text variant="placeholder" color="var(--dark-80)">
          What's your feedback?
        </Text>
        <div className={ReviewSubmissionPanelStyles.scoresContainer}>
          <Score
            onPress={() => setScore(20)}
            isSelected={score === 20}
            value={20}
            label="Feedback"
            description="leave overall feedback"
          />
          <Score
            onPress={() => setScore(30)}
            isSelected={score === 30}
            value={30}
            label="Approve"
            description="and leave feedback"
          />
          <Score
            onPress={() => setScore(10)}
            isSelected={score === 10}
            value={10}
            label="Send back"
            description="for another round of changes"
          />
          <Score
            onPress={() => setScore(InformalFeedbackReviewDismissReason.Declined)}
            isSelected={score === 'declined'}
            value="declined"
            label="Decline"
            description="responding to this review"
          />
        </div>

        <div className={ReviewSubmissionPanelStyles.messageWrapper} data-testid="review-message">
          <ContentComposer
            ref={contentRef}
            content={message}
            placeholder="Add details to your review"
            onChange={setMessage}
          />
        </div>
        {score === InformalFeedbackReviewScore.NeedsMajorChanges && (
          <div className={ReviewSubmissionPanelStyles.dueDate}>
            <Text variant="metadata" color="var(--dark-60)">
              {request?.requester.display_name} should respond to your requests by:
            </Text>
            <DueDatePicker
              minValue={dayjs().format('YYYY-MM-DD')}
              size={isMobile ? 'lg' : 'md'}
              frontIcon={Calendar2}
              displayText={(value) => (
                <>
                  <div>{dayjs(value).format('ddd, MMM D')}</div>
                  {value && <div className={ReviewSubmissionPanelStyles.dateInfo}>{displayDaysRemaining(value)}</div>}
                </>
              )}
              value={dueDate}
              onChange={(value) => value && setDueDate(value)}
              variant="secondary"
              shape="squared"
              showClearDate={false}
            />
          </div>
        )}
        <SubmitButton
          review={review}
          request={request}
          isDisabled={!score || isLoading}
          message={message}
          value={score}
          dueDate={dueDate}
        />
      </div>
    </>
  )
})
